import { useState, useEffect } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Download, BookOpen, FolderOpen } from 'lucide-react';
import { downloadHRDocument, formatDateShort } from './hrUtils';

const DocSection = ({ icon: Icon, iconColor, title, docs, emptyText, testId }) => (
  <Card className="bg-white border-slate-200">
    <CardHeader>
      <CardTitle className="text-slate-800 text-lg flex items-center gap-2">
        <Icon className={`w-5 h-5 ${iconColor}`} /> {title}
        <Badge variant="secondary" className="ml-auto">{docs.length}</Badge>
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-2" data-testid={testId}>
      {docs.length === 0 && <p className="text-sm text-slate-400 py-4">{emptyText}</p>}
      {docs.map((d) => (
        <div key={d.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:border-cyan-300 transition-colors">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{d.title}</p>
            <p className="text-xs text-slate-400">{d.file_name} · {formatDateShort(d.created_at)}</p>
          </div>
          <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => downloadHRDocument(d)}>
            <Download className="w-4 h-4 mr-1" /> Download
          </Button>
        </div>
      ))}
    </CardContent>
  </Card>
);

export const PoliciesTab = () => {
  const [policies, setPolicies] = useState(null);
  const [hrDocs, setHrDocs] = useState(null);

  useEffect(() => {
    Promise.all([
      hrAPI.listDocuments({ category: 'policy' }),
      hrAPI.listDocuments({ category: 'hr_doc' }),
    ]).then(([p, h]) => { setPolicies(p.data); setHrDocs(h.data); })
      .catch(() => { setPolicies([]); setHrDocs([]); });
  }, []);

  if (!policies || !hrDocs) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="hr-policies-tab">
      <DocSection icon={BookOpen} iconColor="text-indigo-500" title="Company Policies" docs={policies}
        emptyText="No policies published yet." testId="hr-policies-list" />
      <DocSection icon={FolderOpen} iconColor="text-emerald-500" title="HR Documents" docs={hrDocs}
        emptyText="No HR documents published yet (handbooks, forms, etc.)." testId="hr-hrdocs-list" />
    </div>
  );
};
